import React, { useMemo, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Copy, Check, ExternalLink, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_ORIGIN = (() => {
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin;
  return BACKEND_URL || '';
})();

const REF_RE = /^\s*((?:[1-3]\s)?[A-Za-z][A-Za-z\s]+?)\s+(\d+):(\d+)(?:-\d+)?\s*$/;

const EmbedBuilder = () => {
  const { language } = useLanguage();
  const isES = language === 'es';
  const [ref, setRef] = useState('John 3:16');
  const [copiedKey, setCopiedKey] = useState(null);

  const valid = REF_RE.test(ref);

  // Load the same embed.js into THIS page once so the preview badges render
  // exactly like they would on a third-party site.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (document.getElementById('lumera-embed-preview-script')) return;
    const s = document.createElement('script');
    s.id = 'lumera-embed-preview-script';
    s.src = `${PUBLIC_ORIGIN}/embed.js`;
    s.defer = true;
    document.body.appendChild(s);
  }, []);

  const snippets = useMemo(() => {
    const safeRef = ref.trim();
    return {
      badge: `<a data-lumera-verse="${safeRef}"></a>\n<script defer src="${PUBLIC_ORIGIN}/embed.js"></script>`,
      autoScan: `<p data-lumera-scan>\n  For God so loved the world (${safeRef}) that he gave his only Son...\n</p>\n<script defer src="${PUBLIC_ORIGIN}/embed.js"></script>`,
      link: `<a href="${PUBLIC_ORIGIN}/open?ref=${encodeURIComponent(safeRef)}&lens=1" target="_blank" rel="noopener noreferrer">Study ${safeRef} on Lumera</a>`,
      url: `${PUBLIC_ORIGIN}/open?ref=${encodeURIComponent(safeRef)}&lens=1`,
    };
  }, [ref]);

  const copy = async (key, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1800);
      toast.success(isES ? '¡Copiado!' : 'Copied to clipboard');
    } catch {
      toast.error(isES ? 'No se pudo copiar' : 'Copy failed');
    }
  };

  // Force the preview badges to re-decorate whenever ref changes
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const els = document.querySelectorAll('[data-lumera-verse]');
    els.forEach((el) => { el.dataset.lumeraDone = ''; el.textContent = ''; });
    // Re-trigger embed.js MutationObserver
    document.body.appendChild(document.createElement('span')).remove();
  }, [ref]);

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-5" data-testid="embed-builder">
      <div className="flex items-center gap-2">
        <Sparkles className="w-6 h-6 text-purple-600" />
        <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
          {isES ? 'Generador de Insignia "Powered by Lumera"' : 'Embed Builder — "Powered by Lumera"'}
        </h1>
      </div>
      <p className="text-sm text-slate-600 dark:text-slate-400">
        {isES
          ? 'Pega un versículo y obtén un fragmento HTML que abre el Lente del Versículo de Lumera directamente desde cualquier blog, boletín o sitio de iglesia.'
          : 'Paste a verse, get an HTML snippet that opens the Lumera Verse Lens panel from any blog, bulletin, or church site.'}
      </p>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{isES ? 'Versículo' : 'Verse reference'}</CardTitle>
          <CardDescription>
            {isES ? 'Formato: "Juan 3:16" o "Romanos 8:28-30"' : 'Format: "John 3:16" or "Romans 8:28-30"'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="embed-ref" className="text-xs font-semibold">{isES ? 'Referencia' : 'Reference'}</Label>
            <Input
              id="embed-ref"
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              placeholder="John 3:16"
              data-testid="embed-ref-input"
            />
            {!valid && (
              <p className="text-xs text-red-600">
                {isES ? 'Referencia no reconocida' : 'Reference not recognized'}
              </p>
            )}
          </div>

          {valid && (
            <div className="rounded-lg border-2 border-dashed border-purple-300 dark:border-purple-700 p-4 bg-purple-50/40 dark:bg-purple-950/20">
              <p className="text-[10px] uppercase tracking-widest text-purple-700 dark:text-purple-300 mb-2">
                {isES ? 'Vista previa' : 'Live preview'}
              </p>
              <div className="flex flex-wrap items-baseline gap-2 text-sm" data-testid="embed-preview">
                <span>
                  {isES
                    ? 'Imagina una entrada de blog que dice "como leemos en '
                    : 'Imagine a blog post that says "as we read in '}
                </span>
                <a data-lumera-verse={ref} />
                <span>{isES ? ', Pablo describe..."' : ', Paul describes…"'}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="badge" className="w-full">
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="badge" data-testid="embed-tab-badge">{isES ? 'Insignia' : 'Badge'}</TabsTrigger>
          <TabsTrigger value="auto" data-testid="embed-tab-auto">{isES ? 'Auto' : 'Auto-scan'}</TabsTrigger>
          <TabsTrigger value="link" data-testid="embed-tab-link">{isES ? 'Enlace' : 'Plain link'}</TabsTrigger>
          <TabsTrigger value="url" data-testid="embed-tab-url">URL</TabsTrigger>
        </TabsList>

        {[
          {
            key: 'badge', value: 'badge',
            title: isES ? 'Insignia con icono Lumera' : 'Branded badge with arrow',
            desc: isES
              ? 'Una insignia con degradado morado→teal que abre el Lente del Versículo en una nueva pestaña.'
              : 'A gradient purple→teal pill that opens the Verse Lens panel in a new tab.',
            snippet: snippets.badge,
          },
          {
            key: 'auto', value: 'auto',
            title: isES ? 'Auto-detectar versículos en un bloque' : 'Auto-detect every verse in a block',
            desc: isES
              ? 'Lumera escanea automáticamente cualquier referencia bíblica dentro del bloque y la convierte en insignia.'
              : 'Lumera auto-scans every Bible reference inside the block and converts it to a clickable badge.',
            snippet: snippets.autoScan,
          },
          {
            key: 'link', value: 'link',
            title: isES ? 'Enlace simple sin script' : 'Plain link — no script needed',
            desc: isES
              ? 'Para sitios estáticos como bulletins de iglesia. No requiere JavaScript.'
              : 'For static church bulletins. No JavaScript required.',
            snippet: snippets.link,
          },
          {
            key: 'url', value: 'url',
            title: isES ? 'Solo la URL' : 'Just the URL',
            desc: isES
              ? 'Para usar en correo, redes sociales, o cualquier campo donde solo cabe una dirección.'
              : 'For email, social, or anywhere a single URL is needed.',
            snippet: snippets.url,
          },
        ].map((tab) => (
          <TabsContent key={tab.key} value={tab.value} className="space-y-3 mt-3">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{tab.title}</CardTitle>
                <CardDescription>{tab.desc}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <pre className="bg-slate-900 text-slate-100 dark:bg-slate-950 text-xs p-3 rounded overflow-x-auto whitespace-pre-wrap break-words" data-testid={`embed-snippet-${tab.key}`}>
                  {tab.snippet}
                </pre>
                <div className="flex gap-2">
                  <Button
                    onClick={() => copy(tab.key, tab.snippet)}
                    variant="default"
                    size="sm"
                    data-testid={`embed-copy-${tab.key}`}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {copiedKey === tab.key ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                    {copiedKey === tab.key
                      ? (isES ? '¡Copiado!' : 'Copied!')
                      : (isES ? 'Copiar fragmento' : 'Copy snippet')}
                  </Button>
                  {tab.key === 'url' && valid && (
                    <Button
                      onClick={() => window.open(snippets.url, '_blank', 'noopener,noreferrer')}
                      variant="outline"
                      size="sm"
                      data-testid="embed-test-open"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      {isES ? 'Abrir prueba' : 'Test it'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      <Card className="bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle className="text-sm">{isES ? 'Cómo funciona' : 'How it works'}</CardTitle>
        </CardHeader>
        <CardContent className="text-xs space-y-2 text-slate-600 dark:text-slate-400">
          <p>{isES
            ? '1. Pega el fragmento en tu blog, sitio de iglesia o boletín.'
            : '1. Drop the snippet anywhere on your blog, church site, or bulletin.'}</p>
          <p>{isES
            ? '2. El script lee cada elemento marcado y lo transforma en una insignia que abre Lumera.'
            : '2. The script reads every marked element and turns it into a clickable badge that opens Lumera.'}</p>
          <p>{isES
            ? '3. Cuando hacen clic, el Lente del Versículo se abre con 7 fuentes (Henry, JFB, Clarke, Gill, TSK, Nave\'s, ISBE).'
            : '3. On click, the Verse Lens panel opens with 7 public-domain sources (Henry, JFB, Clarke, Gill, TSK, Nave\'s, ISBE).'}</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmbedBuilder;
